import { isFeatureEnable } from '../features-enabling';
import {
  generateUrlWithRegion,
  updateQueryStringParameters
} from '../url-utils';
import joinUrl from '@ogury/url-join';
import globalConfig from '../config';
import { getWindowSize } from '../window-size-utils';
import { getPercentBetween } from '../utils';
import ajax from '../AjaxPromises';
import {
  CreativeEvent,
  DisplayEvent,
  MonitoringEventType
} from './analytics.types';
import { MediaErrorCodeConstants } from './analytics.types';
import { logError } from '../sentry';
import { getEffectiveConnectionType } from '../connection';
import { getCurrentRenderTime } from '../render-timer';
import { isWeb } from '../environment-utils';
import { DebugLog } from '../debug-log';
import { IVideoSizes } from '../video-utils';
import { extractZoneFromElem } from '../Events';
import VASTError from '../../../formats/video-sdk3/lib/vast/vast-error';
import {
  formatSupportMraidTag,
  getCreativeType,
  getFormatParams,
  isEventTypeAlwaysEnable
} from './analytics.utils';
import {
  CachedMediaFile,
  MediaFile
} from '../../../formats/video-sdk3/lib/vast/media-file';
import { hasSkin } from '../../../formats/video-sdk3/lib/utils';
import { AdContainerStore } from '../stores/ad-container.store';

const IMPRESSION_TRACK_DELAY = 100;
const VAST_ERROR_DOWNLOAD_EVENT_NAME = 'video_error_vastdownload';

export default class AnalyticsService {
  format: FormatName;
  formatConfig: FormatConfig;
  adContext: AdContext;
  redirectUrl!: string;
  creativeType: string | void;
  formatIsClosed: boolean = false;
  lastInteraction: number = 0;
  limitTimeBetweenAction: number = 30; //ms
  lastEvent!: MouseEvent;
  nobodyCatchEventTimeout!: NodeJS.Timeout;
  canSendImpressionTrack: boolean = true;
  canSendRagequitTrack: boolean = true;
  creativeExpandFirstSent: boolean = false;
  creativeDraggedFirstSent: boolean = false;
  mediaFile!: MediaFile | CachedMediaFile;
  videoSizes: IVideoSizes = {};
  hasVpaid: boolean = false;
  _pendingTracks: number = 0;
  _pendingTracksWaiter: Array<(value: boolean) => void> = [];

  sentTracksMap: Record<string, boolean> = {};

  constructor(
    formatName: FormatName,
    config: FormatConfig,
    adContext: AdContext,
    force: boolean = false
  ) {
    this.adContext = adContext;
    this.format = formatName;
    this.formatConfig = {
      ...config,
      ...(force ? { analytics: { enable: true } } : {})
    };
    this.initBindings();

    this.creativeType = getCreativeType(
      this.format,
      this.formatConfig.creative
    );

    if (isFeatureEnable('rageQuitEvent')) {
      window.mraid.addEventListener(
        'ogyOnCloseSystem',
        this.handleOgyOnCloseSystem
      );
    }

    if (formatSupportMraidTag(this.format)) {
      const body = document.querySelector('body');
      body && body.setAttribute('data-zone', 'format');

      this.limitTimeBetweenAction = 130;
      window.addEventListener('click', this.storeEventForAnalytics, true);
    }
  }

  setMediaFile(mediaFile: MediaFile | CachedMediaFile) {
    this.mediaFile = mediaFile;
  }

  setVideoSizes(videoSizes: IVideoSizes) {
    this.videoSizes = videoSizes;
  }

  initBindings(): void {
    this.cleanAnalyticsStoredEventSending = this.cleanAnalyticsStoredEventSending.bind(
      this
    );
    this.endAnalytics = this.endAnalytics.bind(this);
    this.handleOgyOnCloseSystem = this.handleOgyOnCloseSystem.bind(this);
    this.storeEventForAnalytics = this.storeEventForAnalytics.bind(this);
    this.sendCreativeTrack = this.sendCreativeTrack.bind(this);
    this.sendAnalyticsTrack = this.sendAnalyticsTrack.bind(this);
    this.sendMraidAnalyticsTrack = this.sendMraidAnalyticsTrack.bind(this);
    this.shouldSendTrack = this.shouldSendTrack.bind(this);
    this.rejectDoubleClick = this.rejectDoubleClick.bind(this);
    this.sendCreativeOptoutTrack = this.sendCreativeOptoutTrack.bind(this);
    this.sendCreativeVideoErrorAutoPlay = this.sendCreativeVideoErrorAutoPlay.bind(
      this
    );
    this.sendCreativeVideoErrorVastDownload = this.sendCreativeVideoErrorVastDownload.bind(
      this
    );
    this.sendCreativeVideoError = this.sendCreativeVideoError.bind(this);
    this.disableImpressionTrack = this.disableImpressionTrack.bind(this);
    this.disableRagequitTrack = this.disableRagequitTrack.bind(this);
    this.sendCustomTrack = this.sendCustomTrack.bind(this);
    this.sendCreativeDragged = this.sendCreativeDragged.bind(this);
    this.setHasVpaid = this.setHasVpaid.bind(this);
  }

  handleOgyOnCloseSystem(): void {
    window.mraid.removeEventListener(
      'ogyOnCloseSystem',
      this.handleOgyOnCloseSystem
    );
    if (
      window.mraid &&
      window.mraid.getState() !== 'loading' &&
      this.adContext &&
      (this.adContext.adUnitType === 'interstitial' ||
        this.adContext.adUnitType === 'optin_video')
    ) {
      this.sendRagequitTrack();
    }
  }

  disableImpressionTrack(): void {
    this.canSendImpressionTrack = false;
  }

  disableRagequitTrack(): void {
    this.canSendRagequitTrack = false;
  }

  setHasVpaid(hasVpaid: boolean): void {
    this.hasVpaid = hasVpaid;
  }

  endAnalytics(): void {
    // warning for setTimeouts if using in mraid formats
    if (formatSupportMraidTag(this.format)) {
      window.removeEventListener('click', this.storeEventForAnalytics, true);
    }
  }

  /**
   * store event to send it anyway, even if mraid.open or mraid.close are not called
   */
  storeEventForAnalytics(e: MouseEvent): void {
    this.lastEvent = e;
    this.nobodyCatchEventTimeout = setTimeout(() => {
      DebugLog.log('Nobody catch the stored event, send as dry click');
      this.sendAnalyticsTrack(this.lastEvent);
    }, 130);
  }

  sendMraidAnalyticsTrack(
    closeSource?: string,
    accomplished: boolean = false,
    forceZone?: string,
    eventType = 'interaction'
  ): void {
    // this timeout allow body.onclick to storeEventForAnalytics
    setTimeout(() => {
      // cancel the auto send after delay
      clearTimeout(this.nobodyCatchEventTimeout);
      this.sendAnalyticsTrack(
        this.lastEvent,
        closeSource,
        accomplished,
        forceZone,
        eventType
      );
    }, 10);
  }

  // used for output common component which can be in mraid or not
  cleanAnalyticsStoredEventSending(): MouseEvent {
    setTimeout(() => {
      clearTimeout(this.nobodyCatchEventTimeout);
    }, 20);
    return this.lastEvent;
  }

  sendCreativeTrack(
    eventType: CreativeEvent,
    event?: any,
    delayBeforeInteraction?: number
  ) {
    const url = this.getCreativeTrackUrl(
      eventType,
      event,
      delayBeforeInteraction
    );
    DebugLog.log(`sendCreativeTrack: ${eventType}`, { event });
    this.sendTrackUrl(url);
  }

  getCreativeTrackUrl(
    eventType: CreativeEvent,
    event?: any,
    delayBeforeInteraction?: number,
    redirectUrl?: string
  ): string | undefined {
    if (window.isOgyPreview) {
      return;
    }

    if (!this.formatConfig) {
      return;
    }

    const data = this.getCreativeTrackUrlParameters(
      this.adContext,
      eventType,
      event,
      delayBeforeInteraction,
      redirectUrl
    );

    const url = this.getCreativeTrackUrlFromDataParameters(data);

    return url;
  }

  getCreativeTrackUrlFromDataParameters(data: any) {
    const url = updateQueryStringParameters(
      joinUrl(
        generateUrlWithRegion(
          globalConfig.FORMAT_ANALYTICS_ENDPOINT,
          this.adContext.region
        ),
        'creative'
      ),
      data
    );

    return url;
  }

  getCreativeTrackUrlParameters(
    adContext: AdContext,
    eventType: CreativeEvent,
    event?: any,
    delayBeforeInteraction?: number,
    redirectUrl?: string
  ) {
    const timer = delayBeforeInteraction || getCurrentRenderTime();

    const datas: any = {
      /* eslint-disable camelcase */
      e: eventType,
      timer: timer || undefined,
      fr: redirectUrl || '',
      ...this.getCommonQueryParameters(adContext)
      /* eslint-enable camelcase */
    };

    if (
      ['client_click', 'client_ignored_click'].indexOf(eventType) !== -1 &&
      event
    ) {
      const coordinates = this.getClientClickCoordinates(event, eventType);
      if (
        coordinates &&
        typeof coordinates.pos !== 'undefined' &&
        typeof coordinates.pos.x !== 'undefined' &&
        typeof coordinates.pos.y !== 'undefined'
      ) {
        datas.click_x = coordinates.pos.x;
        datas.click_y = coordinates.pos.y;

        datas.click_pc_x = getPercentBetween(
          coordinates.pos.x,
          event.view.innerWidth
        );
        datas.click_pc_y = getPercentBetween(
          coordinates.pos.y,
          event.view.innerHeight
        );
      }
    }

    if (eventType === VAST_ERROR_DOWNLOAD_EVENT_NAME && event.e_fetch_error) {
      datas.fetch_err = event.e_fetch_error;
    }

    return datas;
  }

  getDisplayTrackUrl(eventType: DisplayEvent | string): string {
    if (window.isOgyPreview) {
      return '';
    }
    if (!this.formatConfig || !this.formatConfig.displayTrackers) {
      return '';
    }
    if (
      this.formatConfig.displayTrackers.inhouse === true ||
      eventType === 'display' ||
      eventType.indexOf('omid_') === 0 ||
      eventType.indexOf('vpaid_') === 0 ||
      eventType.indexOf('[event]') === 0
    ) {
      const datas: Object = {
        /* eslint-disable camelcase */
        e: eventType,
        ...this.getCommonQueryParameters()
        /* eslint-enable camelcase */
      };

      const url = updateQueryStringParameters(
        joinUrl(
          generateUrlWithRegion(
            globalConfig.MS_ADS_MONITORING_EVENTS_URL,
            this.adContext.region
          ),
          'viewability'
        ),
        datas
      );

      return url;
    }
    return '';
  }

  sendVideoMonitoringEvent(eventName: string): void {
    if (window.isOgyPreview) {
      return;
    }
    let app_ssp = (this.adContext._appExchange || '').toUpperCase();

    // In stats index, ogury ssp is in lowercase
    if (app_ssp === 'OGURY') {
      app_ssp = 'ogury';
    }

    const device = getWindowSize();

    const params: any = {
      e_campaign_id: this.adContext.campaignId,
      app: this.adContext.apiKey,
      app_sdk_version:
        (window.MRAID_ENV && window.MRAID_ENV.sdkVersion) || 'unknown',
      advertiser_id: this.adContext.advertiserId,
      e_advert_id: this.adContext.impressionId,
      app_ssp,
      e_creative_id: this.adContext.creativeId,
      screen_height: screen.height || 0,
      screen_width: screen.width || 0,
      device_model: this.adContext.deviceModel || 'unknown',
      has_vpaid: this.hasVpaid ? 1 : 0,
      with_skin: hasSkin(this.formatConfig) ? 1 : 0,
      orientation: device.width > device.height ? 'landscape' : 'portrait',
      connectivity: getEffectiveConnectionType(),
      ad_unit_type: this.adContext.adUnitType,
      advert_delivery_type: this.adContext.advertDeliveryType,
      device_width: device.width ? device.width : 0,
      device_height: device.height ? device.height : 0,
      device_os: this.adContext.deviceOs,
      env: isWeb() ? 'web' : 'inapp',
      video_event: eventName,
      video_url:
        this.mediaFile &&
        this.mediaFile.url &&
        this.mediaFile.url.toLowerCase().indexOf('base64') === -1 &&
        this.mediaFile.url.toLowerCase().indexOf('blob:') === -1
          ? this.mediaFile.url.substring(0, 256)
          : 'unknown',
      video_duration: this.mediaFile ? this.mediaFile.duration : 0,
      video_mime_type: this.mediaFile ? this.mediaFile.type : 'unknown',
      video_bitrate:
        this.mediaFile && 'bitrate' in this.mediaFile
          ? this.mediaFile.bitrate
          : 0,
      format_name: this.adContext.formatName || 'unknown',
      format_size_response: this.adContext.formatSizeResponse || 'unknown',
      prog_creative_id: this.adContext.advertiserCreativeId || 'unknown',
      ...this.videoSizes
    };

    if (
      this.adContext.adUnitType === 'in_article' &&
      this.adContext.inArticleType
    ) {
      params.in_article_type = this.adContext.inArticleType;
    }

    // return params;
    this.sendEvent(params, eventName, 'video');
  }

  sendEvent(
    eventParams: Object,
    eventName: string,
    eventType: MonitoringEventType
  ): void {
    DebugLog.log(`sendMonitoringEvent: ${eventName}`, eventParams);

    const url = updateQueryStringParameters(
      joinUrl(
        generateUrlWithRegion(
          globalConfig.MS_ADS_MONITORING_EVENTS_URL,
          this.adContext.region
        ),
        eventType
      ),
      eventParams
    );

    this.sendTrackUrl(url);
  }

  sendMonitoringVideoStartedEvent(): void {
    this.sendVideoMonitoringEvent('started');
  }

  sendMonitoringVideoCompleteEvent(): void {
    this.sendVideoMonitoringEvent('complete');
  }

  sendMonitoringVideoChosenEvent(): void {
    this.sendVideoMonitoringEvent('chosen');
  }

  sendMonitoringVideoChosenNewVideoSelectionEvent(): void {
    this.sendVideoMonitoringEvent('chosen_new_video_selection');
  }

  sendMonitoringVideoVastBlockingEvent(): void {
    this.sendVideoMonitoringEvent('vast_blocking');
  }

  sendMonitoringVideoVastMRCViewableEvent(): void {
    this.sendVideoMonitoringEvent('vast_mrc_viewable');
  }

  sendMonitoringVideoVastTimeoutEvent(): void {
    this.sendVideoMonitoringEvent('vast_download_timeout');
  }

  getCommonQueryParameters(adContext?: AdContext): Object {
    if (!adContext) {
      adContext = this.adContext;
    }

    return {
      /* eslint-disable camelcase */
      oc_id: parseInt(adContext.campaignId),
      ak: adContext.apiKey,
      auid: adContext.adUnitId,
      imp: adContext.impressionId,
      a_sdk:
        (window.MRAID_ENV && window.MRAID_ENV.sdkVersion) ||
        adContext.sdkVersion ||
        undefined,
      u_id: adContext.userId,
      t_u: adContext.isTemporaryUser,
      u_tk: adContext.userInstanceToken,
      u_os: adContext.deviceOs,
      dmn: adContext.domain,
      pg: adContext.page,
      ad_ob: adContext.adObject,
      conn:
        window.navigator &&
        window.navigator.connection &&
        window.navigator.connection.type
          ? window.navigator.connection.type.toUpperCase()
          : 'WIFI',
      cr_id: adContext.creativeId,
      dsp: adContext.dsp,
      a_ex: adContext._appExchange,
      a_b: adContext.appBundle
      /* eslint-enable camelcase */
    };
  }

  sendImpressionFormat(adContainerStore: AdContainerStore): void {
    if (window.isOgyPreview) {
      return;
    }

    if (this.format !== 'AdChooser') {
      adContainerStore.triggerOnAdImpression();
    }

    /* https://jira.ogury.io/browse/SPY-15465
      The impressionFormatTrack feature-enabling is deactivate on external traffic
      This means, when MRAID_ENV.sdk = 'presage'
      For Equativ, Google Open Bidding, One Tag, this value can vary
      For now we for this impression format to not be sent for Beeswax only (the only case we had)
      before a specific revamp of this in both stack
     */
    if (this.adContext.dsp === 'beeswax') {
      return;
    }

    const queryParams = this.getCommonQueryParameters();

    DebugLog.log('sendImpressionFormat', queryParams);

    const url = updateQueryStringParameters(
      joinUrl(
        generateUrlWithRegion(
          globalConfig.FORMAT_ANALYTICS_ENDPOINT,
          this.adContext.region
        ),
        'impfmt'
      ),
      queryParams
    );

    setTimeout(() => {
      if (this.canSendImpressionTrack) {
        this.sendTrackUrl(url);
        this.disableImpressionTrack();
      }
    }, IMPRESSION_TRACK_DELAY);
  }

  generateAnalyticsData = () => {
    /* eslint-disable camelcase */
    const device = getWindowSize();

    let app_ssp = (this.adContext._appExchange || '').toUpperCase();

    // In stats index, ogury ssp is in lowercase
    if (app_ssp === 'OGURY') {
      app_ssp = app_ssp.toLowerCase();
    }

    return {
      advert_id: this.adContext.impressionId,
      device,
      params: getFormatParams(this.formatConfig, this.format, this.redirectUrl),
      orientation: device.width > device.height ? 'landscape' : 'portrait',
      format: this.format,
      format_version: globalConfig.COMMIT_HASH,
      format_name: this.adContext.formatName || 'unknown',
      sdk_version:
        (window.MRAID_ENV && window.MRAID_ENV.sdkVersion) || 'unknown',
      campaign_id: parseInt(this.adContext.campaignId),
      app_api_key: this.adContext.apiKey,
      app_ssp,
      creative_id: this.adContext.creativeId,
      app_bundle: this.adContext.appBundle
    };
    /* eslint-enable camelcase */
  };

  sendAnalyticsTrack(
    event: any,
    closeSource?: string,
    accomplished: boolean = false,
    forceZone?: string,
    eventType = 'interaction',
    delayBeforeInteraction?: number,
    browserParams?: any,
    params?: any
  ): void {
    if (window.isOgyPreview) {
      return;
    }
    // after clic on continue on VFS
    if (this.formatIsClosed && closeSource === 'continue_button') {
      forceZone = 'wait_close';
      closeSource = undefined;
    }

    if (this.formatIsClosed && closeSource) {
      closeSource += '_already_close';
    }

    if (this.shouldSendTrack(event, closeSource, eventType) === false) {
      return;
    }
    /* eslint-disable camelcase */
    let datas: any = this.generateAnalyticsData();
    if (browserParams) datas.browser_params = browserParams;
    datas.event = eventType;
    datas.delay_before_interaction = delayBeforeInteraction
      ? delayBeforeInteraction
      : getCurrentRenderTime();
    datas.is_accomplished = accomplished;

    if (closeSource) {
      datas.close_source = closeSource;
    }
    if (this.creativeType) {
      datas.creative_type = this.creativeType;
    }
    // detect TouchEvent
    if (event) {
      datas = {
        ...datas,
        ...this.getClientClickCoordinates(event, eventType, forceZone)
      };
    }
    /* eslint-enable camelcase */

    if (closeSource) {
      this.endAnalytics();
    }

    datas.params = { ...datas.params, ...params };

    DebugLog.log('sendAnalyticsTrack', datas);

    ajax
      .postJson(
        joinUrl(
          generateUrlWithRegion(
            globalConfig.MS_ADS_MONITORING_EVENTS_URL,
            this.adContext.region
          ),
          'track'
        ),
        datas
      )
      .then((res: any) => DebugLog.log('sendAnalyticsTrack: event sent', res))
      .catch(logError);
  }

  getClientClickCoordinates(
    event: any,
    eventType: string,
    forceZone?: string
  ): any {
    const datas: any = {};
    if (
      event.touches &&
      event.touches.length &&
      typeof event.touches[0].clientX !== 'undefined' &&
      typeof event.touches[0].clientY !== 'undefined'
    ) {
      datas.pos = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY
      };
      datas.zone = forceZone || extractZoneFromElem(event.touches[0].target);
      datas.interaction = 'screen_touch';
    } else if (
      typeof event.clientX !== 'undefined' &&
      typeof event.clientY !== 'undefined'
    ) {
      // detect MouseEvent
      datas.pos = {
        x: event.clientX,
        y: event.clientY
      };
      datas.zone = forceZone || extractZoneFromElem(event.target);
      datas.interaction = 'screen_click';
    } else if (forceZone) {
      datas.zone = forceZone;
      datas.interaction = 'screen_click';
    } else if (eventType === 'interaction') {
      datas.zone = 'system_button';
      datas.interaction = 'system_button';
    }

    return datas;
  }

  sendOmidTrackingEvents(omidTrackingEvents: Record<string, any>): void {
    if (window.isOgyPreview) {
      return;
    }

    // send event 1/100
    if (Math.floor(Math.random() * 100) !== 1) {
      return;
    }

    const datas: any = {
      ...this.generateAnalyticsData(),
      ...omidTrackingEvents
    };

    datas.event = 'omid_tracking';

    ajax
      .postJson(
        joinUrl(
          generateUrlWithRegion(
            globalConfig.MS_ADS_MONITORING_EVENTS_URL,
            this.adContext.region
          ),
          'omid_tracking'
        ),
        datas
      )
      .then((res: any) =>
        DebugLog.log('sendOmidTrackingEvents: event sent', res)
      )
      .catch(logError);
  }

  sendCustomTrack(
    eventType: string,
    delayBeforeInteraction?: number,
    params?: any
  ): void {
    this.sendAnalyticsTrack(
      {},
      undefined,
      false,
      undefined,
      eventType,
      delayBeforeInteraction,
      undefined,
      params
    );
  }

  shouldSendTrack(
    event: any,
    closeSource?: string,
    eventType?: string
  ): boolean {
    if (!this.format) {
      DebugLog.log('Format is not initialized, call initAnalytics()');
      return false;
    }
    if (this.format === 'Banner' && this.creativeType === 'mraid') {
      DebugLog.log(
        'The banner format does not support Analytics with mraid creatives'
      );
      return false;
    }
    if (!this.formatConfig) {
      DebugLog.log('no format config');
      return false;
    }
    if (
      !isEventTypeAlwaysEnable(eventType) &&
      !this.formatConfig.analytics.enable &&
      !this.isAnalyticsForced() &&
      eventType !== 'browser'
    ) {
      DebugLog.log('analytics not enabled on this campaign');
      return false;
    }
    if (!event) {
      DebugLog.log('no event found');
      return false;
    }
    if (this.rejectDoubleClick(eventType)) {
      DebugLog.log('double event ignored');
      return false;
    }
    if (closeSource) {
      this.formatIsClosed = true;
    }
    return true;
  }

  isAnalyticsForced(): boolean {
    // Force analytics for Trivia Crack to understand performance drop
    return this.adContext.apiKey === '271047';
  }

  rejectDoubleClick(eventType?: string): boolean {
    if (eventType !== 'interaction') {
      return false;
    }
    const now = Date.now();
    if (now - this.lastInteraction < this.limitTimeBetweenAction) {
      return true;
    } else {
      this.lastInteraction = now;
      return false;
    }
  }

  checkAndSendNoUserIdTrack(): void {
    if (!this.adContext.userId || typeof this.adContext.userId !== 'string') {
      this.sendCustomTrack('error_no_user_id');
    }
  }

  sendRagequitTrack() {
    if (this.canSendRagequitTrack) {
      this.sendCreativeTrack('ragequit');
    }
  }

  sendCreativeCloseButtonClick() {
    this.sendCreativeTrack('close_button_click');
  }

  sendCreativeClientClick() {
    this.sendCreativeTrack('client_click');
  }

  sendCreativeViewableTrack() {
    if (window && window.mraid && window.mraid.isViewable()) {
      this.sendCreativeTrack('viewable');
    }
  }

  sendCreativeClientIgnoredClick(e?: MouseEvent | MessageEvent) {
    this.sendCreativeTrack('client_ignored_click', e);
  }

  sendCreativeOptoutTrack(): void {
    this.sendCreativeTrack('optout');
  }

  sendCreativeVideoErrorAutoPlay(): void {
    this.sendCreativeTrack('video_error_autoplay');
  }

  sendCreativeVideoErrorVastDownload(error: VASTError): void {
    this.sendCreativeTrack(VAST_ERROR_DOWNLOAD_EVENT_NAME, {
      ...(error &&
        error.e_fetch_error && { e_fetch_error: error.e_fetch_error })
    });
  }

  sendCreativeVideoErrorPrefetch(): void {
    this.sendCreativeTrack('video_error_prefetch');
  }

  sendCreativeExpand(isExpanded: boolean): void {
    if (isExpanded) {
      this.sendCreativeTrack('collapse');
    } else {
      if (!this.creativeExpandFirstSent) {
        this.sendCreativeTrack('expand_first');
        this.creativeExpandFirstSent = true;
      }

      this.sendCreativeTrack('expand');
    }
  }

  sendCreativeTrackLoadInit(): void {
    this.sendCreativeTrack('load_init');
  }

  sendCreativeLoadError(): void {
    this.sendCreativeTrack('load_error');
  }

  sendCreativeVideoError(error: MediaError): void {
    const { code } = error;
    switch (code) {
      case MediaErrorCodeConstants.MEDIA_ERR_ABORTED:
        this.sendCreativeTrack('video_error_aborted');
        break;
      case MediaErrorCodeConstants.MEDIA_ERR_NETWORK:
        this.sendCreativeTrack('video_error_network');
        break;
      case MediaErrorCodeConstants.MEDIA_ERR_DECODE:
        this.sendCreativeTrack('video_error_decode');
        break;
      case MediaErrorCodeConstants.MEDIA_ERR_SRC_NOT_SUPPORTED:
        this.sendCreativeTrack('video_error_unsupported');
        break;
      default:
        this.sendCreativeTrack('video_error');
    }
  }

  sendCreativeDragged(): void {
    if (
      this.adContext.adUnitType === 'overlay_thumbnail' &&
      this.adContext.isAdUnitDraggable &&
      !this.creativeDraggedFirstSent
    ) {
      this.creativeDraggedFirstSent = true;
      this.sendCreativeTrack('dragged');
    }
  }

  sendCreativeHeaderMinimized(): void {
    this.sendCreativeTrack('header_minimized');
  }

  sendCreativeHeaderMinimizedDefault(): void {
    this.sendCreativeTrack('header_minimized_default');
  }

  sendCreativeHeaderAdBelowHeader(): void {
    this.sendCreativeTrack('header_ad_below_header');
  }

  sendCreativeHeaderAdAboveHeader(): void {
    this.sendCreativeTrack('header_ad_above_header');
  }

  sendCreativeHeaderOptimized(): void {
    this.sendCreativeTrack('header_optimized');
  }

  sendCreativeVpaidSelected(): void {
    const track = 'vpaid_selected';
    if (this.sentTracksMap[track]) {
      return;
    }
    this.sentTracksMap[track] = true;
    this.sendCreativeTrack(track);
  }

  sendCreativeLoaded(): void {
    this.sendCreativeTrack('loaded');
  }

  sendEndScreenTrack(endScreen: any, event: string) {
    if (!endScreen) return;
    this.sendAnalyticsTrack(
      {},
      undefined,
      false,
      undefined,
      'end_screen_' + event,
      undefined,
      undefined,
      {
        end_screen_type: endScreen.type,
        end_screen_duration: endScreen.duration,
        end_screen_origin: endScreen.origin
      }
    );
  }

  sendStoreKitTrack(event: string, errorCode?: number) {
    const params: any = {};
    if (errorCode) params.errorCode = errorCode;
    this.sendAnalyticsTrack(
      {},
      undefined,
      false,
      undefined,
      'store_kit_' + event,
      undefined,
      undefined,
      params
    );
  }

  waitUntilTracksAreSent(): Promise<unknown> {
    const waitPromise = new Promise((resolve: (value: boolean) => void) => {
      if (this._pendingTracks === 0) return resolve(true);
      this._pendingTracksWaiter.push(resolve);
    });

    const timeoutPromise = new Promise(resolve =>
      setTimeout(() => resolve(false), 500)
    );

    return Promise.race([waitPromise, timeoutPromise]);
  }

  sendTrackUrl(url?: string): void {
    if (!url) return;

    this._pendingTracks++;
    const pixel = new Image();
    const callback = () => {
      if (--this._pendingTracks > 0) return;
      this._pendingTracksWaiter.forEach((resolver: (value: boolean) => void) =>
        resolver(true)
      );
      this._pendingTracksWaiter = [];
    };
    pixel.onload = callback;
    pixel.onerror = callback;
    pixel.src = url;
  }
}
